import { DateFormatter } from 'infrastructure/functions';
import { useQueryClient } from '@tanstack/react-query';
import { BroadcastTopics } from 'services/wsService';
import { useWebsocketRoom } from 'infrastructure/hooks/websockets/use-room';
import { useSocketOn } from 'infrastructure/hooks/websockets/use-socket-on';

import { getRpmInteractionTimeKey } from './get-rpm-interactive-time';

import type { Res } from './get-rpm-interactive-time';
import type { Nullable } from 'infrastructure/types';

interface ISocketData {
  billingMonth: number;
  billingYear: number;
  clinicGuid: Nullable<string>;
  patientGuid: string;
  time: number;
}

interface Props {
  patientGuid: string;
}

export const useRealTimeUpdateInteractiveTime = ({ patientGuid }: Props) => {
  useWebsocketRoom('rpmPatientProfile', { patientGuid });
  useSocketOn(BroadcastTopics.InteractiveTimeInsert, (data: ISocketData) => {
    updateInteractionTime(data);
  });

  const queryClient = useQueryClient();
  const today = new Date();
  const formattedDate = DateFormatter({ date: today });
  const month = today.getUTCMonth() + 1;
  const year = today.getUTCFullYear();

  const updateInteractionTime = (socketData: ISocketData) => {
    const { billingMonth, billingYear } = socketData;

    if (
      patientGuid !== socketData.patientGuid &&
      month !== billingMonth &&
      year !== billingYear
    )
      return;

    const queryKey = getRpmInteractionTimeKey(
      socketData.patientGuid,
      formattedDate,
    );

    queryClient.setQueryData<Res>(queryKey, (old) => {
      if (!old) return old;

      if (socketData.clinicGuid) {
        old.clinicTime += socketData.time;
        old.totalTime += socketData.time;
      } else {
        old.totalTime += socketData.time;
      }

      return old;
    });
  };

  return { updateInteractionTime };
};
